import { useState, useRef } from "react";

import { fetchReceiptPdf } from "@/api/pdf";
import { formatDateForFilename } from "@/utils/dateFormatting";

import { Receipt } from "@/types/receipt";

interface UsePdfDownloadProps {
  user_id: string;
  zeipt_receipt_transnr: string;
  receipt: Receipt;
  lang: string;
}

export const usePdfDownload = ({
  user_id,
  zeipt_receipt_transnr,
  receipt,
  lang,
}: UsePdfDownloadProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const linkRef = useRef<HTMLAnchorElement | null>(null);
  const urlRef = useRef<string | null>(null);

  const downloadPdf = async () => {
    setIsLoading(true);
    try {
      const isDemo = receipt.demo === true;
      const pdfBlob = await fetchReceiptPdf(
        user_id,
        zeipt_receipt_transnr,
        lang,
        isDemo
      );

      // Clean up previous URL if it exists
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
      }

      const pdfUrl = URL.createObjectURL(pdfBlob);
      urlRef.current = pdfUrl;

      const link = document.createElement("a");
      link.href = pdfUrl;

      const date = formatDateForFilename(
        receipt.timestamp,
        receipt.merchant.country_code
      );

      link.download = `${date}-${receipt.merchant.purchase_location?.store_name}-${receipt.receipt_number}.pdf`;
      document.body.appendChild(link);
      linkRef.current = link;

      // Trigger download
      link.click();

      // Clean up
      document.body.removeChild(link);
      setIsLoading(false);

      return true;
    } catch (error) {
      console.error("PDF download error:", error);
      alert("An error occurred while generating the PDF");
      setIsLoading(false);
      return false;
    }
  };

  return { isLoading, downloadPdf };
};
